/**
 * ฟอร์มซิม — ยกมาจากโมดัลใน SimPage (หน้าเต็ม 28 ต.ค. 2026 · ต้นแบบ components/company/companyForm.ts)
 * ใช้ร่วมกันระหว่าง SimViewPage (อ่านอย่างเดียว) และ SimFormPage (เพิ่ม/แก้ไข)
 */
import { Sim, SimStatus, SimType } from '../../services/simService';
import type { SimPackageOption } from '../../services/simPackageService';
import { normalizeSimPhone, parsePhoneNumbers } from '../../utils/simPhone';
import { getSimStatusLabel, getSimTypeLabel } from '../../utils/simHelpers';

export type SimFormMode = 'create' | 'edit';

/**
 * ค่ายมือถือของซิม — single source of truth ใช้ร่วมกันทั้ง "ฟอร์ม" และ "ตัวกรอง"
 *
 * B-C9-2: เดิมแยกกันคนละที่ ⇒ ฟอร์มมี 5 ค่า แต่ตัวกรองมี 4 (ตก "อื่นๆ") ทำให้ซิมที่ค่าย
 * = OTHER กรองหาไม่เจอเลย — อาการเดียวกับ C9 (ค่าย NT) แค่คนละค่า
 *
 * value เป็นตัวพิมพ์ใหญ่ให้ตรงกับ:
 *   - ข้อมูลจริงใน DB ทั้ง 691 แถว (TRUE 676 / AIS 13 / DTAC 2 — ตรวจด้วย BINARY แล้ว
 *     ไม่มีแถวไหนเก็บเป็น 'True' เลย)
 *   - ค่าที่ Excel import เขียน (excel_import_service.py → "TRUE"/"AIS"/"DTAC")
 *   - enum `Carrier` ของแพ็กเกจซิม ⇒ map carrier → operator กลายเป็น 1:1 ไม่ต้องแปลงค่า
 * เดิมฟอร์มบันทึกเป็น 'True'/'Other' ซึ่งไม่ตรงกับใครเลย (รอดมาได้เพราะ MySQL collation
 * ไม่สนตัวพิมพ์) — ถ้าปล่อยไว้ ข้อมูลจะเริ่มปนตัวพิมพ์เมื่อมีคนบันทึกผ่านฟอร์ม
 */
export const SIM_OPERATOR_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'TRUE', label: 'True' },
  { value: 'AIS', label: 'AIS' },
  { value: 'DTAC', label: 'DTAC' },
  { value: 'NT', label: 'NT' },
  { value: 'OTHER', label: 'อื่นๆ' },
];

/**
 * ตัวเลือกค่ายสำหรับ "ฟอร์ม" ของซิมแถวหนึ่ง ๆ — ต้องมีตัวเลือกที่ถือ "ค่าปัจจุบันของแถวนั้น"
 * เสมอ ถึงแม้ค่านั้นจะไม่อยู่ในชุดมาตรฐาน
 *
 * ทำไมต้องมี: ข้อมูลจริงบน **production** ยังมีตัวพิมพ์ปนอยู่ (operator = 'True' 75 แถว
 * และ NULL 2 แถว จาก 757) — ต่างจาก dev ที่เป็นตัวพิมพ์ใหญ่ล้วน ถ้า <Select> มีแต่ชุด
 * มาตรฐาน พอเปิดฟอร์มแก้ไขแถวพวกนี้ ค่าปัจจุบันจะไม่ตรง option ไหนเลย → select ไม่มีอะไร
 * ถูกเลือก/เด้งไปตัวแรก → ผู้ใช้เห็นค่ายผิด และถ้ากดบันทึกก็เสี่ยงเปลี่ยนค่าโดยไม่ตั้งใจ
 * (โดยเฉพาะ 2 แถวที่เป็น NULL ซึ่งไม่มีใครรู้ว่าค่ายอะไร จะถูกยัดเป็น TRUE)
 *
 * หลักการเดียวกับตอน map carrier→operator: "ถ้าไม่รู้จัก ให้คงค่าเดิมไว้ อย่าเดาแทนผู้ใช้"
 * ⚠️ ตั้งใจ **ไม่** normalize ข้อมูลเก่าให้อัตโนมัติตอนเปิดฟอร์ม — การแก้ข้อมูล 75 แถวบน prod
 * ต้องเป็นการตัดสินใจของเจ้าของระบบ ไม่ใช่ผลข้างเคียงของการเปิดหน้าจอ
 */
export const getSimOperatorOptions = (
  current?: string | null
): ReadonlyArray<{ value: string; label: string }> => {
  const value = (current ?? '').trim();

  // ยังไม่ได้ระบุค่าย (NULL/ว่าง) → ต้องมีตัวเลือกว่างให้เห็นตามจริง ไม่ใช่เด้งไปค่ายแรก
  if (!value) {
    return [{ value: '', label: '— ไม่ระบุ —' }, ...SIM_OPERATOR_OPTIONS];
  }

  // ต้องเทียบแบบ **ตรงตัวพิมพ์** เพราะ <select> จับคู่ option ด้วยค่าที่ตรงเป๊ะเท่านั้น —
  // 'True' ไม่แมทช์ option ที่ value='TRUE' (ถึงจะเป็นค่ายเดียวกันในสายตาคนก็ตาม)
  // ถ้าเทียบแบบไม่สนตัวพิมพ์ที่นี่ จะคิดว่า "รู้จักแล้ว" แต่ select ยังเลือกอะไรไม่ได้อยู่ดี
  const isExactMatch = SIM_OPERATOR_OPTIONS.some((o) => o.value === value);
  if (isExactMatch) return SIM_OPERATOR_OPTIONS;

  // label บอกให้ชัดว่าเป็นค่าที่บันทึกไว้เดิม ผู้ใช้จะได้รู้ว่าทำไมมี "True" สองบรรทัด
  // และเลือกบรรทัดมาตรฐานเองได้ถ้าตั้งใจจะแก้ให้เป็นค่ามาตรฐาน
  return [...SIM_OPERATOR_OPTIONS, { value, label: `${value} (ค่าเดิมในระบบ)` }];
};

/**
 * "ค่ายมือถือ" เป็นฟิลด์บังคับหรือไม่ — ขึ้นกับว่าแถวนั้นเคยมีค่ายอยู่แล้วหรือเปล่า
 *
 * บังคับเมื่อ: สร้างซิมใหม่ · หรือแก้ไขแถวที่ "มีค่ายอยู่แล้ว" (กันคนลบค่ายทิ้งโดยไม่ตั้งใจ)
 * ไม่บังคับเมื่อ: เปิดแก้ไขแถวที่ค่ายว่างอยู่แต่เดิม (prod มี 2 แถว operator = NULL)
 *
 * เหตุผล (PO ตัดสิน): คนที่มาแก้ "เบอร์โทร" ของซิมแถวนั้น ไม่มีทางรู้ว่าค่ายอะไร —
 * บังคับตอนนั้นได้แค่การเดา ซึ่งแย่กว่า NULL ที่ซื่อสัตย์ว่า "ไม่รู้" · การบังคับให้ตัดสินใจ
 * ได้ผลก็ต่อเมื่อคนตรงหน้ามีข้อมูลพอจะตัดสิน · และถ้าล็อกไว้ก็จะกลายเป็นบั๊กหน้าตาเดียวกับ
 * ที่เพิ่งรายงานกันเอง ("ซิมที่ code เป็น NULL แก้จากหน้าจอไม่ได้เลย")
 * backend ไม่ได้บังคับ field นี้อยู่แล้ว การบังคับที่ฟอร์มจึงไม่ได้ปกป้อง invariant ใด ๆ
 * ⇒ ข้อมูลใหม่ยังสะอาด · ข้อมูลเก่าไม่ถูกล็อก · ไม่มีใครถูกบังคับให้เดา
 */
export const isSimOperatorRequired = (
  modalMode: 'create' | 'edit' | 'view',
  originalOperator?: string | null
): boolean => modalMode === 'create' || Boolean((originalOperator ?? '').trim());

export interface SimFormData {
  code: string;
  phone_number: string;
  serial_number: string;
  operator: string;
  sim_type: SimType;
  monthly_fee: number | null;
  // แพ็กเกจซิม — เลือกจาก master (sim_packages); BE set package_name ให้
  sim_package_id: number | null;
  speed_mbps: number | null;
  billing_cycle: 'MONTHLY' | 'YEARLY';
  yearly_cost: number | null;
  activation_code: string;
  registered_by_user_id: number | null;
  activation_date: string;
  expiry_date: string;
  status: SimStatus;
  notes: string;
}

export const emptySimForm = (code = '', registeredBy: number | null = null): SimFormData => ({
  code,
  phone_number: '',
  serial_number: '',
  operator: SIM_OPERATOR_OPTIONS[0].value, // B-C9-2: 'TRUE' ตรงกับ enum/ข้อมูลจริง
  sim_type: SimType.POSTPAID,
  monthly_fee: 0,
  sim_package_id: null,
  speed_mbps: null,
  billing_cycle: 'MONTHLY',
  yearly_cost: null,
  activation_code: '',
  registered_by_user_id: registeredBy,
  activation_date: '',
  expiry_date: '',
  // R224: a NEW sim is AVAILABLE (พร้อมใช้งาน), not ACTIVE/IN_USE (deprecated alias)
  status: SimStatus.AVAILABLE,
  notes: '',
});

/**
 * ซิมจาก API → ค่าฟอร์ม · mode 'edit' ล้างวันหมดอายุของซิมรายเดือน (ช่องถูกซ่อน ถ้าปล่อยไว้จะ submit
 * ค่าที่มองไม่เห็น/ชน validation วันที่) · หน้าดูใช้ simToForm(sim) แสดงค่าจริง (legacy ที่ยังค้างก็เห็น)
 */
export const simToForm = (sim: Sim, mode: SimFormMode | 'view' = 'view'): SimFormData => ({
  code: sim.code || '',
  phone_number: sim.phone_number,
  serial_number: sim.serial_number || '',
  operator: sim.operator,
  sim_type: sim.sim_type,
  // บั๊กชุดเดียวกับ C8: ฟอร์มนี้ "ไม่มีช่องกรอกค่าบริการรายเดือน" (ถูกถอดออกไปแล้ว —
  // ค่ามาจากแพ็กเกจซิมแทน) แต่เดิมเขียน `|| 0` แล้ว simService ส่ง 0 ขึ้นไปทุกครั้ง
  // (0 ผ่าน guard `!== null`) ⇒ ซิมที่ monthly_fee เป็น NULL ถูกทับเป็น 0 ตอนกดแก้ไข
  // เรื่องอื่น — บน dev มี 686 จาก 693 ซิมที่เป็น NULL = โดนกันเกือบทั้งตาราง
  // ใช้ `??` คง NULL ไว้ → simService ข้าม field นี้ → ค่าเดิมอยู่ครบ
  monthly_fee: sim.monthly_fee ?? null,
  sim_package_id: sim.sim_package_id || null,
  speed_mbps: sim.speed_mbps || null,
  billing_cycle: (sim.billing_cycle as 'MONTHLY' | 'YEARLY') || 'MONTHLY',
  yearly_cost: sim.yearly_cost || null,
  activation_code: sim.activation_code || '',
  registered_by_user_id: sim.registered_by_user_id || null,
  activation_date: sim.activation_date ? sim.activation_date.split('T')[0] : '',
  // ซิมรายเดือนไม่มีวันหมดอายุ — ล้างค่า legacy ที่ค้างตั้งแต่โหลดฟอร์ม (เฉพาะโหมดแก้ไข)
  expiry_date: mode === 'edit' && sim.sim_type === SimType.POSTPAID ? '' : (sim.expiry_date ? sim.expiry_date.split('T')[0] : ''),
  status: sim.status,
  notes: sim.notes || '',
});

/** กรองแพ็กเกจตามประเภทซิม (Suraluck 2026-08-25): รายเดือน→MONTHLY · รายปี→YEARLY · BE ปฏิเสธ (400) ถ้าไม่ตรง */
export const packageMatchesSimType = (pkg: SimPackageOption, simType: SimType): boolean => {
  if (simType === SimType.POSTPAID) return pkg.billing_cycle === 'MONTHLY';
  if (simType === SimType.YEARLY) return pkg.billing_cycle === 'YEARLY';
  return true; // PREPAID / CORPORATE / OTHER เลือกได้ทุกแพ็กเกจ
};

/** ป้ายราคาแพ็กเกจตามรอบบิล — แพ็กเกจรายปีแสดง ฿/ปี ไม่ใช่ ฿/เดือน */
export const packagePriceLabel = (pkg: Pick<SimPackageOption, 'billing_cycle' | 'yearly_cost' | 'monthly_cost'>): string =>
  pkg.billing_cycle === 'YEARLY'
    ? `฿${pkg.yearly_cost?.toLocaleString() || '0'}/ปี`
    : `฿${pkg.monthly_cost?.toLocaleString() || '0'}/เดือน`;

/** ตรวจฟอร์ม · create รองรับหลายเบอร์ (textarea) · edit เบอร์เดียว */
export const validateSimForm = (
  formData: SimFormData,
  mode: SimFormMode,
  original?: Pick<Sim, 'operator'> | null
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!formData.code) {
    errors.code = 'กรุณาใส่รหัสซิม';
  }
  if (mode === 'create') {
    const phones = parsePhoneNumbers(formData.phone_number);
    if (phones.length === 0) {
      errors.phone_number = 'กรุณาใส่เบอร์โทรศัพท์';
    } else {
      const bad = phones.filter((p) => !/^[0-9]{10}$/.test(p));
      if (bad.length > 0) errors.phone_number = `เบอร์ต้องเป็นตัวเลข 10 หลัก: ${bad.join(', ')}`;
    }
  } else if (!formData.phone_number) {
    errors.phone_number = 'กรุณาใส่เบอร์โทรศัพท์';
  } else if (!/^[0-9]{10}$/.test(normalizeSimPhone(formData.phone_number))) {
    errors.phone_number = 'เบอร์โทรศัพท์ต้องเป็นตัวเลข 10 หลัก';
  }
  // ไม่บังคับกับแถวเก่าที่ค่ายว่างอยู่แต่เดิม — ไม่งั้นแก้ฟิลด์อื่นของแถวนั้นไม่ได้เลย
  // (ค่าว่างจะไม่ถูกส่งขึ้น backend อยู่แล้ว: simService guard `if (rawData.operator?.trim())`)
  if (!formData.operator && isSimOperatorRequired(mode, original?.operator)) {
    errors.operator = 'กรุณาเลือกค่ายมือถือ';
  }
  if (!formData.sim_type) {
    errors.sim_type = 'กรุณาเลือกประเภทซิม';
  }
  // บังคับเลือกแพ็กเกจซิมตอน "สร้างใหม่" เท่านั้น — แถว legacy ที่ sim_package_id เป็น NULL ต้อง save ได้
  if (mode === 'create' && !formData.sim_package_id) {
    errors.sim_package_id = 'กรุณาเลือกแพ็กเกจซิม';
  }
  if (!formData.status) {
    errors.status = 'กรุณาเลือกสถานะ';
  }
  if (formData.monthly_fee !== null && formData.monthly_fee !== undefined && formData.monthly_fee < 0) {
    errors.monthly_fee = 'ค่าบริการรายเดือนต้องเป็นค่า 0 หรือมากกว่า';
  }
  if (formData.activation_date && formData.expiry_date) {
    if (new Date(formData.activation_date) > new Date(formData.expiry_date)) {
      errors.activation_date = 'วันที่เปิดใช้งานต้องไม่มากกว่าวันหมดอายุ';
    }
  }
  return errors;
};

/** ชื่อฟิลด์ภาษาไทย (การ์ด "ประวัติกิจกรรม") — คีย์ตรงกับ backend services/item_audit.SIM_SNAPSHOT_FIELDS */
export const SIM_FIELD_LABELS: Record<string, string> = {
  code: 'รหัสซิม',
  phone_number: 'เบอร์โทรศัพท์',
  serial_number: 'Serial (ICCID)',
  operator: 'ค่ายมือถือ',
  sim_type: 'ประเภทซิม',
  sim_package_id: 'แพ็กเกจซิม',
  package_name: 'ชื่อแพ็กเกจ',
  monthly_fee: 'ค่าบริการรายเดือน',
  speed_mbps: 'ความเร็ว (Mbps)',
  billing_cycle: 'รอบบิล',
  yearly_cost: 'ค่าใช้จ่ายรายปี',
  activation_code: 'รหัส Activate',
  registered_by_user_id: 'ผู้ลงทะเบียน',
  activation_date: 'วันที่เปิดใช้งาน',
  expiry_date: 'วันหมดอายุ',
  status: 'สถานะ',
  notes: 'หมายเหตุ',
};

export const SIM_VALUE_FORMATTERS: Record<string, (v: unknown) => string> = {
  status: (v) => getSimStatusLabel(String(v)),
  sim_type: (v) => getSimTypeLabel(String(v)),
  billing_cycle: (v) => (String(v).toUpperCase() === 'YEARLY' ? 'รายปี' : 'รายเดือน'),
};
